import { Table } from '@chakra-ui/react';
import {
  ProductTypeEnum,
  RateBillingFrequencyEnum,
  SubscriptionViewItem,
} from '@monetize/types/app';
import { CurrencyCodes } from '@monetize/types/common';
import { formatCurrency, formatInteger } from '@monetize/utils/core';
import { RATE_BILLING_FREQUENCY_SHORTCODE_DISPLAY } from '../../constants/offerings';
import { getShortCodeForUnitPriceFrequency } from '../../utils/quotes';
import { TableCell } from './SubscriptionViewTable';

const ONE_TIME_FREQUENCIES = new Set<RateBillingFrequencyEnum>([
  RateBillingFrequencyEnum.ONETIME,
]);

const ONE_TIME_PRODUCT_TYPES = new Set<ProductTypeEnum>([
  ProductTypeEnum.ONETIME,
  ProductTypeEnum.ONETIME_PREPAID_CREDIT,
  ProductTypeEnum.ONETIME_TOKEN,
  ProductTypeEnum.DISCOUNT,
]);

const HIDE_QUANTITY_PRODUCT_TYPES = new Set<ProductTypeEnum>([
  ProductTypeEnum.USAGE,
  ProductTypeEnum.MIN_COMMIT,
]);

function formatItemAmount(
  item: SubscriptionViewItem,
  currency: CurrencyCodes,
  billingFrequency: RateBillingFrequencyEnum | null | undefined,
  billingFrequencyInMonths: number | null | undefined,
): string {
  const isUsage = item.productType === ProductTypeEnum.USAGE;
  const isMinCommit = item.productType === ProductTypeEnum.MIN_COMMIT;

  if (
    isUsage &&
    item.displayUnitPriceAfterDiscount == null &&
    item.isVariableUnitPrice
  ) {
    return '*/unit';
  }

  const rawAmount = isMinCommit
    ? item.unitPrice * item.quantity * (billingFrequencyInMonths ?? 1)
    : (item.displayUnitPriceAfterDiscount ?? 0);
  const amountStr = formatCurrency(rawAmount, { currency });

  // No frequency suffix for one-time products or one-time-billed offerings.
  if (
    !billingFrequency ||
    ONE_TIME_FREQUENCIES.has(billingFrequency) ||
    ONE_TIME_PRODUCT_TYPES.has(item.productType)
  ) {
    return amountStr;
  }

  if (item.displayUnitPriceFrequency) {
    return `${amountStr}/${getShortCodeForUnitPriceFrequency(item.displayUnitPriceFrequency)}`;
  }

  if (isUsage) {
    return `${amountStr}/unit`;
  }

  const shortCode =
    RATE_BILLING_FREQUENCY_SHORTCODE_DISPLAY[billingFrequency]?.(
      billingFrequencyInMonths,
    ) ?? '';

  return shortCode ? `${amountStr}/${shortCode}` : amountStr;
}

interface SubscriptionItemRowsProps {
  items: SubscriptionViewItem[];
  currency: CurrencyCodes;
  billingFrequency?: RateBillingFrequencyEnum | null;
  billingFrequencyInMonths?: number | null;
  isRamp?: boolean;
  isCurrentRamp?: boolean;
}

export const SubscriptionItemRows = ({
  items,
  currency,
  billingFrequency,
  billingFrequencyInMonths,
  isRamp = false,
  isCurrentRamp = false,
}: SubscriptionItemRowsProps) => {
  const accentColor = isCurrentRamp ? 'tIndigo.base' : 'border.subtle';

  return (
    <>
      {items.map((item) => (
        <Table.Row
          key={item.quoteItemId}
          borderBottom="1px solid #e4e4e4"
          borderLeft={isRamp ? '3px solid' : undefined}
          borderLeftColor={isRamp ? accentColor : undefined}
        >
          <TableCell px={4} textProps={{ pl: isRamp ? 8 : 4 }}>
            {item.productName ?? '—'}
          </TableCell>
          <TableCell textAlign="end">
            {HIDE_QUANTITY_PRODUCT_TYPES.has(item.productType)
              ? ''
              : formatInteger(item.quantity)}
          </TableCell>
          <TableCell textAlign="end" px={4}>
            {formatItemAmount(
              item,
              currency,
              billingFrequency,
              billingFrequencyInMonths,
            )}
          </TableCell>
        </Table.Row>
      ))}
    </>
  );
};
